import { SeoPageConfig } from "./seo-pages";

/**
 * Посадочные страницы под запросы вида «расшифровка речи на <языке>».
 * Казахский, узбекский, армянский и грузинский поддерживаются моделью
 * AssemblyAI Universal-2, то есть только в режиме «Качество», — тексты это учитывают.
 */
export const LANGUAGE_PAGES: SeoPageConfig[] = [
  {
    path: "/russian-to-text",
    metaTitle: "Расшифровка русской речи в текст онлайн | Транскрибация на русском",
    metaDescription:
      "Транскрибация русской речи в текст с автоматической пунктуацией и разделением по спикерам. Точность 95–99 % на записях хорошего качества.",
    customTitle: "Расшифровка русской речи в текст",
    customSubtitle: "Автоматическая пунктуация, заглавные буквы и разделение по спикерам — текст готов к чтению.",
    seoDescription: (
      <>
        <p>
          Русский язык — основной для сервиса, и настроен он именно под него. Модель не просто переводит звук в
          последовательность слов: она расставляет запятые и точки, оформляет прямую речь и начинает предложения с
          заглавной буквы. Это тот случай, когда расшифровку можно читать сразу, не тратя час на вычитку.
        </p>
        <p>
          Отдельная сложность русского — падежи и свободный порядок слов: одно неверно распознанное окончание меняет
          смысл фразы. Современные модели учитывают контекст всего предложения, а не отдельных слов, поэтому такие
          ошибки встречаются редко даже в записях с профессиональной терминологией.
        </p>
        <p>
          Сервис справляется и со смешанной речью, когда в русский текст вплетаются английские термины — обычная
          ситуация для совещаний в IT, маркетинге и финансах.
        </p>
      </>
    ),
    faq: [
      {
        question: "Расставляются ли знаки препинания автоматически?",
        answer:
          "Да. Точки, запятые, вопросительные знаки и заглавные буквы проставляются автоматически — расшифровку можно читать сразу после обработки."
      },
      {
        question: "Насколько точно распознаётся русская речь?",
        answer:
          "На записях хорошего качества точность достигает 95–99 %. На результат влияют чёткость дикции, уровень фонового шума и качество микрофона."
      },
      {
        question: "Что будет с англоязычными терминами внутри русской речи?",
        answer:
          "Они распознаются и остаются в тексте. Смешанная речь — типичный случай для рабочих совещаний, и модель к ней готова."
      },
      {
        question: "Распознаются ли региональные особенности произношения?",
        answer:
          "Да, модель обучена на разнообразной речи и устойчива к акцентам и говорам. Сильный акцент может немного снизить точность, но текст останется читаемым."
      }
    ]
  },
  {
    path: "/english-to-text",
    metaTitle: "Расшифровка английской речи в текст | Транскрибация English онлайн",
    metaDescription:
      "Транскрибация английской речи в текст с помощью ИИ. Подходит для лекций, интервью и деловых созвонов, устойчива к разным акцентам.",
    customTitle: "Расшифровка английской речи в текст",
    customSubtitle: "Лекции, интервью и деловые созвоны на английском — с таймкодами и разметкой спикеров.",
    seoDescription: (
      <>
        <p>
          Английский — самый изученный язык для систем распознавания речи, и точность на нём стабильно высокая. Модель
          уверенно работает не только с эталонным произношением, но и с индийским, китайским или восточноевропейским
          акцентом — то есть с тем, как английский реально звучит на международных созвонах.
        </p>
        <p>
          Типичные сценарии — расшифровка зарубежных лекций и подкастов для конспекта, интервью с иностранными
          собеседниками и записи рабочих встреч с распределённой командой. Разделение по спикерам помогает не запутаться
          в том, кто что сказал, а таймкоды позволяют быстро вернуться к нужному фрагменту записи.
        </p>
      </>
    ),
    faq: [
      {
        question: "Справляется ли сервис с неродным произношением?",
        answer:
          "Да. Модель обучена на речи носителей самых разных акцентов, поэтому международные созвоны распознаются уверенно."
      },
      {
        question: "Переводится ли английская речь на русский?",
        answer:
          "Нет, расшифровка выполняется на языке оригинала. Готовый текст можно перевести любым удобным сервисом — это будет заметно проще, чем переводить со слуха."
      },
      {
        question: "Можно ли получить субтитры к англоязычному видео?",
        answer: "Да, результат выгружается в SRT с таймкодами — файл подходит для загрузки в YouTube и другие сервисы."
      }
    ]
  },
  {
    path: "/kazakh-to-text",
    metaTitle: "Расшифровка казахской речи в текст онлайн | Транскрибация на казахском",
    metaDescription:
      "Транскрибация казахской речи в текст с помощью ИИ. Расшифровка совещаний, интервью и лекций на казахском языке в режиме «Качество».",
    customTitle: "Расшифровка казахской речи в текст",
    customSubtitle: "Совещания, интервью и лекции на казахском — выберите режим «Качество» при загрузке.",
    seoDescription: (
      <>
        <p>
          Инструментов автоматической расшифровки казахской речи заметно меньше, чем для английского или русского, а
          ручная транскрибация часового разговора занимает три-четыре часа работы. Сервис закрывает эту задачу
          автоматически: загрузите запись, выберите казахский язык — и получите готовый текст.
        </p>
        <p>
          Важный момент: казахский поддерживается моделью повышенной точности, поэтому при загрузке нужно выбрать режим
          «Качество». Он работает чуть дольше быстрого режима, зато именно он умеет работать с казахской речью.
        </p>
        <p>
          Часто встречается смешанная казахско-русская речь — обычное дело для рабочих совещаний. Такие записи
          обрабатываются, но на переключениях между языками возможны отдельные неточности, поэтому итог стоит бегло
          просмотреть.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как расшифровать запись на казахском?",
        answer:
          "Загрузите файл, выберите в списке языков казахский и обязательно укажите режим «Качество» — именно он поддерживает казахскую речь."
      },
      {
        question: "Почему нужен режим «Качество»?",
        answer:
          "Быстрый режим работает на модели, где казахского языка нет. Режим «Качество» использует модель с его поддержкой, поэтому для казахского он единственный рабочий вариант."
      },
      {
        question: "Что будет со смешанной казахско-русской речью?",
        answer:
          "Такие записи обрабатываются, но на стыках языков возможны ошибки. Если переключения частые, попробуйте режим автоопределения языка и сравните результат."
      },
      {
        question: "Расставляются ли знаки препинания?",
        answer: "Да, пунктуация и деление на предложения проставляются автоматически."
      }
    ]
  },
  {
    path: "/uzbek-to-text",
    metaTitle: "Расшифровка узбекской речи в текст онлайн | Транскрибация на узбекском",
    metaDescription:
      "Транскрибация узбекской речи в текст с помощью ИИ. Расшифровка интервью, совещаний и лекций на узбекском языке в режиме «Качество».",
    customTitle: "Расшифровка узбекской речи в текст",
    customSubtitle: "Интервью, совещания и лекции на узбекском — в режиме повышенной точности.",
    seoDescription: (
      <>
        <p>
          Автоматическая расшифровка узбекской речи до недавнего времени была почти недоступна: большинство сервисов
          ограничивались десятком крупнейших языков. Сейчас узбекский поддерживается моделью повышенной точности, и
          часовое интервью превращается в текст за несколько минут вместо нескольких часов ручной работы.
        </p>
        <p>
          При загрузке выберите узбекский язык и режим «Качество» — быстрый режим этот язык не обрабатывает. Результат
          приходит с таймкодами и разметкой говорящих, поэтому с ним удобно работать дальше: цитировать, сокращать,
          готовить материал к публикации.
        </p>
      </>
    ),
    faq: [
      {
        question: "Какой режим выбрать для узбекского языка?",
        answer:
          "Только «Качество». Быстрый режим использует модель без поддержки узбекского, и корректного результата она не даст."
      },
      {
        question: "Распознаётся ли речь на латинице и кириллице?",
        answer:
          "Распознаётся именно звучащая речь, а не написание, поэтому вопрос алфавита к записи не относится. Текст возвращается в стандартной для языка графике."
      },
      {
        question: "Можно ли разделить реплики нескольких собеседников?",
        answer: "Да, автоматическая диаризация помечает реплики как «Спикер 1», «Спикер 2» и так далее."
      }
    ]
  },
  {
    path: "/armenian-to-text",
    metaTitle: "Расшифровка армянской речи в текст онлайн | Транскрибация на армянском",
    metaDescription:
      "Транскрибация армянской речи в текст с помощью ИИ. Расшифровка интервью, лекций и совещаний на армянском языке в режиме «Качество».",
    customTitle: "Расшифровка армянской речи в текст",
    customSubtitle: "Интервью, лекции и совещания на армянском — с таймкодами и разметкой спикеров.",
    seoDescription: (
      <>
        <p>
          Армянский относится к языкам, для которых готовых инструментов расшифровки почти нет, поэтому расшифровывать
          интервью и лекции обычно приходилось вручную. Модель повышенной точности армянскую речь поддерживает, и запись
          обрабатывается автоматически — примерно за десятую часть своей длительности.
        </p>
        <p>
          При загрузке выберите армянский язык и режим «Качество»: быстрый режим работает на другой модели, где этого
          языка нет. Готовый текст выгружается в TXT, DOCX или PDF — можно сразу отдавать в работу редактору или
          исследователю.
        </p>
      </>
    ),
    faq: [
      {
        question: "Как расшифровать запись на армянском?",
        answer:
          "Загрузите файл, выберите армянский язык и режим «Качество». Через несколько минут получите текст с таймкодами."
      },
      {
        question: "Почему быстрый режим не подходит?",
        answer:
          "Он работает на модели, в которой армянского языка нет. Поддержка есть только у модели повышенной точности."
      },
      {
        question: "В каких форматах можно скачать расшифровку?",
        answer: "TXT, DOCX, PDF, а для субтитров — SRT."
      }
    ]
  },
  {
    path: "/georgian-to-text",
    metaTitle: "Расшифровка грузинской речи в текст онлайн | Транскрибация на грузинском",
    metaDescription:
      "Транскрибация грузинской речи в текст с помощью ИИ. Расшифровка интервью, совещаний и лекций на грузинском языке в режиме «Качество».",
    customTitle: "Расшифровка грузинской речи в текст",
    customSubtitle: "Интервью, совещания и лекции на грузинском — автоматически, без ручной расшифровки.",
    seoDescription: (
      <>
        <p>
          Грузинский — язык со своей письменностью и фонетикой, непохожей на индоевропейскую, и универсальные модели
          распознавания долго с ним не работали. Сейчас поддержка есть в модели повышенной точности, так что расшифровка
          интервью или лекции перестала быть ручной работой на несколько часов.
        </p>
        <p>
          Порядок тот же, что и для других языков этой группы: загрузите запись, выберите грузинский и режим «Качество».
          На выходе — текст с таймкодами и разделением по говорящим, пригодный для цитирования и дальнейшей обработки.
        </p>
      </>
    ),
    faq: [
      {
        question: "Какой режим нужен для грузинского языка?",
        answer: "Режим «Качество» — только он использует модель с поддержкой грузинской речи."
      },
      {
        question: "Возвращается ли текст грузинским письмом?",
        answer: "Да, расшифровка приходит в оригинальной письменности языка."
      },
      {
        question: "Сколько занимает обработка часовой записи?",
        answer: "Около 5–7 минут: обработка длится примерно 10–15 % от длительности исходной записи."
      }
    ]
  },
  {
    path: "/ukrainian-to-text",
    cardTitle: "Украинский",
    metaTitle: "Расшифровка украинской речи в текст | Транскрибация на украинском",
    metaDescription:
      "Транскрибация украинской речи в текст с автоматической пунктуацией, таймкодами и разделением по спикерам. Работают оба режима обработки.",
    customTitle: "Расшифровка украинской речи в текст",
    customSubtitle: "Пунктуация, таймкоды и разделение по спикерам — как и для русского.",
    seoDescription: (
      <>
        <p>
          Украинский поддерживается в обоих режимах обработки: быстрый справится с чистой записью за считанные минуты,
          режим «Качество» стоит выбирать для сложного звука — шумного помещения, телефонной линии или нескольких
          говорящих одновременно.
        </p>
        <p>
          Как и с русским, сложность языка в развитой системе окончаний: неверно распознанная концовка слова меняет
          смысл фразы. Модель опирается на контекст всего предложения, поэтому такие ошибки редки, но в записях с
          профессиональной лексикой текст стоит вычитывать.
        </p>
        <p>
          Смешанная речь, где украинский чередуется с русским или английскими терминами, распознаётся — на
          переключениях между языками возможны отдельные неточности.
        </p>
      </>
    ),
    faq: [
      {
        question: "Доступны ли оба режима обработки?",
        answer:
          "Да. Быстрый режим подойдёт для чистой записи, «Качество» — для шумного звука, телефонных разговоров и записей с несколькими говорящими."
      },
      {
        question: "Как обрабатывается смешанная украинско-русская речь?",
        answer:
          "Она распознаётся, но на переключениях между языками возможны неточности. Для таких записей стоит попробовать автоопределение."
      },
      {
        question: "Расставляются ли знаки препинания?",
        answer: "Да, пунктуация и заглавные буквы проставляются автоматически."
      }
    ]
  },
  {
    path: "/german-to-text",
    cardTitle: "Немецкий",
    metaTitle: "Расшифровка немецкой речи в текст | Транскрибация на немецком",
    metaDescription:
      "Транскрибация немецкой речи в текст: лекции, интервью и деловые созвоны. Автоматическая пунктуация, таймкоды, разделение по спикерам.",
    customTitle: "Расшифровка немецкой речи в текст",
    customSubtitle: "Лекции, интервью и деловые созвоны на немецком — с таймкодами и разметкой спикеров.",
    seoDescription: (
      <>
        <p>
          Немецкий распознаётся уверенно: язык хорошо изучен системами распознавания речи, а его чёткая фонетика этому
          только помогает. Доступны оба режима обработки — быстрый для чистых записей и «Качество» для сложного звука.
        </p>
        <p>
          Отдельная особенность языка — составные существительные, которые могут занимать полстроки. Модель собирает их
          корректно, опираясь на контекст, хотя редкие узкоспециальные термины стоит проверить при вычитке.
        </p>
        <p>
          Разговорные варианты и региональное произношение распознаются, но сильный диалект снижает точность заметнее,
          чем в других языках, — литературная речь даёт лучший результат.
        </p>
      </>
    ),
    faq: [
      {
        question: "Справляется ли модель с составными словами?",
        answer:
          "Да, длинные составные существительные собираются корректно. Редкие технические термины стоит проверить при вычитке."
      },
      {
        question: "Как обстоит дело с диалектами?",
        answer:
          "Литературная речь распознаётся лучше всего. Сильный региональный диалект заметно снижает точность — для таких записей выбирайте режим «Качество»."
      },
      {
        question: "Переводится ли текст на русский?",
        answer: "Нет, расшифровка выполняется на языке оригинала. Готовый текст можно перевести отдельно."
      }
    ]
  },
  {
    path: "/french-to-text",
    cardTitle: "Французский",
    metaTitle: "Расшифровка французской речи в текст | Транскрибация на французском",
    metaDescription:
      "Транскрибация французской речи в текст с автоматической пунктуацией и диакритикой. Таймкоды и разделение по спикерам включены.",
    customTitle: "Расшифровка французской речи в текст",
    customSubtitle: "Диакритика, пунктуация и разделение по спикерам — текст готов к чтению.",
    seoDescription: (
      <>
        <p>
          Французский — один из наиболее уверенно распознаваемых языков. Диакритические знаки проставляются
          автоматически, что для французского принципиально: без них текст читается тяжело, а часть слов меняет
          значение.
        </p>
        <p>
          Сложность языка для распознавания — связывание слов в потоке речи, из-за которого границы между ними на слух
          размываются. Модель разбирается с этим по контексту предложения, поэтому текст получается связным даже при
          быстрой речи.
        </p>
        <p>
          Доступны оба режима обработки. Для интервью и созвонов с несколькими участниками имеет смысл выбирать
          «Качество» — разделение по спикерам на нём точнее.
        </p>
      </>
    ),
    faq: [
      {
        question: "Проставляются ли диакритические знаки?",
        answer: "Да, автоматически. Это важно для французского: без них часть слов меняет значение."
      },
      {
        question: "Влияет ли беглая речь на точность?",
        answer:
          "Связывание слов усложняет распознавание, но модель опирается на контекст предложения, поэтому текст остаётся связным."
      },
      {
        question: "Какой режим выбрать для интервью?",
        answer: "«Качество» — на нём точнее и распознавание, и разделение по спикерам."
      }
    ]
  },
  {
    path: "/spanish-to-text",
    cardTitle: "Испанский",
    metaTitle: "Расшифровка испанской речи в текст | Транскрибация на испанском",
    metaDescription:
      "Транскрибация испанской речи в текст с учётом разных вариантов языка. Автоматическая пунктуация, таймкоды, разделение по спикерам.",
    customTitle: "Расшифровка испанской речи в текст",
    customSubtitle: "Европейский и латиноамериканский испанский — с пунктуацией и таймкодами.",
    seoDescription: (
      <>
        <p>
          Испанский распознаётся стабильно высоко: у языка прозрачное соответствие между звучанием и написанием, что
          заметно упрощает задачу модели. Работают оба режима обработки.
        </p>
        <p>
          Модель обучена на разных вариантах языка, поэтому справляется и с европейским произношением, и с
          латиноамериканским. Различия в лексике между регионами передаются как есть — сервис не приводит текст к
          какому-то одному стандарту.
        </p>
        <p>
          Как и в других языках, на результат сильнее всего влияет не вариант испанского, а качество записи:
          разборчивость речи и уровень фонового шума.
        </p>
      </>
    ),
    faq: [
      {
        question: "Поддерживается ли латиноамериканский испанский?",
        answer:
          "Да, модель обучена на разных вариантах языка. Региональная лексика передаётся как есть, без приведения к единому стандарту."
      },
      {
        question: "Насколько точно распознаётся испанский?",
        answer:
          "На записях хорошего качества точность высокая — у языка прозрачное соответствие между произношением и написанием."
      },
      {
        question: "Работают ли оба режима обработки?",
        answer: "Да. Быстрый — для чистых записей, «Качество» — для шумного звука и нескольких говорящих."
      }
    ]
  },
  {
    path: "/italian-to-text",
    cardTitle: "Итальянский",
    metaTitle: "Расшифровка итальянской речи в текст | Транскрибация на итальянском",
    metaDescription:
      "Транскрибация итальянской речи в текст с автоматической пунктуацией, таймкодами и разделением по спикерам. Доступны оба режима обработки.",
    customTitle: "Расшифровка итальянской речи в текст",
    customSubtitle: "Интервью, лекции и созвоны на итальянском — с таймкодами и разметкой по спикерам.",
    seoDescription: (
      <>
        <p>
          Итальянский относится к языкам, где написание близко к произношению, и распознавание на нём работает
          уверенно. Пунктуация и заглавные буквы проставляются автоматически, текст читается сразу после обработки.
        </p>
        <p>
          Основная сложность — темп речи: итальянский часто звучит быстро, а слова в потоке сливаются. Модель
          опирается на контекст предложения, поэтому границы слов восстанавливаются корректно даже при высоком темпе.
        </p>
        <p>
          Региональное произношение распознаётся, хотя сильный диалект снижает точность. Для таких записей стоит
          выбирать режим «Качество».
        </p>
      </>
    ),
    faq: [
      {
        question: "Влияет ли быстрый темп речи на точность?",
        answer:
          "Модель восстанавливает границы слов по контексту предложения, поэтому высокий темп сам по себе не проблема."
      },
      {
        question: "Что с региональными диалектами?",
        answer: "Они распознаются, но сильный диалект снижает точность. В таких случаях выбирайте режим «Качество»."
      },
      {
        question: "В каком виде приходит результат?",
        answer: "Текст с пунктуацией, таймкодами и разделением по спикерам. Выгрузка — TXT, DOCX, PDF или SRT."
      }
    ]
  },
  {
    path: "/turkish-to-text",
    cardTitle: "Турецкий",
    metaTitle: "Расшифровка турецкой речи в текст | Транскрибация на турецком",
    metaDescription:
      "Транскрибация турецкой речи в текст с автоматической пунктуацией и таймкодами. Подходит для интервью, созвонов и деловых переговоров.",
    customTitle: "Расшифровка турецкой речи в текст",
    customSubtitle: "Переговоры, интервью и созвоны на турецком — с разметкой по спикерам.",
    seoDescription: (
      <>
        <p>
          Турецкий поддерживается в обоих режимах обработки. Язык агглютинативный: к корню слова последовательно
          присоединяются суффиксы, и одно слово может нести смысл целой фразы. Для распознавания это означает, что
          ошибка в окончании меняет содержание сильнее, чем в европейских языках.
        </p>
        <p>
          Современные модели учитывают контекст всего предложения, поэтому такие ошибки редки. Тем не менее записи с
          деловой или технической терминологией стоит вычитывать — особенно там, где важны точные формулировки.
        </p>
        <p>
          Пунктуация и заглавные буквы проставляются автоматически, реплики разделяются по говорящим, а результат
          выгружается в TXT, DOCX, PDF или SRT.
        </p>
      </>
    ),
    faq: [
      {
        question: "Насколько надёжно распознаются длинные словоформы?",
        answer:
          "Модель опирается на контекст предложения, поэтому суффиксальные цепочки собираются корректно. Терминологию стоит проверить при вычитке."
      },
      {
        question: "Доступны ли оба режима обработки?",
        answer: "Да. Для шумных записей и разговоров с несколькими участниками лучше выбирать «Качество»."
      },
      {
        question: "Переводится ли текст?",
        answer: "Нет, расшифровка остаётся на языке оригинала."
      }
    ]
  },
  {
    path: "/polish-to-text",
    cardTitle: "Польский",
    metaTitle: "Расшифровка польской речи в текст | Транскрибация на польском",
    metaDescription:
      "Транскрибация польской речи в текст с автоматической пунктуацией, диакритикой и таймкодами. Разделение по спикерам включено.",
    customTitle: "Расшифровка польской речи в текст",
    customSubtitle: "Пунктуация, диакритика и разделение по спикерам — текст готов к работе.",
    seoDescription: (
      <>
        <p>
          Польский поддерживается в обоих режимах. Диакритические знаки проставляются автоматически — без них текст
          читался бы с трудом, а часть слов меняла бы значение.
        </p>
        <p>
          Как и другие славянские языки, польский сложен для распознавания развитой системой падежей и обилием
          шипящих, которые на записи среднего качества легко спутать. Режим «Качество» здесь даёт заметно более
          стабильный результат, чем быстрый.
        </p>
        <p>
          Смешанная речь с русскими или английскими вкраплениями распознаётся; на переключениях между языками
          возможны отдельные неточности.
        </p>
      </>
    ),
    faq: [
      {
        question: "Проставляется ли диакритика?",
        answer: "Да, автоматически. Для польского это принципиально — без неё часть слов меняет значение."
      },
      {
        question: "Какой режим выбрать?",
        answer:
          "Для записей среднего качества — «Качество»: обилие шипящих делает польский чувствительным к чистоте звука."
      },
      {
        question: "Распознаётся ли смешанная речь?",
        answer: "Да, но на переключениях между языками возможны неточности."
      }
    ]
  },
  {
    path: "/chinese-to-text",
    cardTitle: "Китайский",
    metaTitle: "Расшифровка китайской речи в текст | Транскрибация на китайском",
    metaDescription:
      "Транскрибация китайской речи в текст иероглифами, с таймкодами и разделением по спикерам. Подходит для переговоров, лекций и интервью.",
    customTitle: "Расшифровка китайской речи в текст",
    customSubtitle: "Переговоры, лекции и интервью на китайском — текст иероглифами с таймкодами.",
    seoDescription: (
      <>
        <p>
          Китайский распознаётся с записью иероглифами, а не транскрипцией латиницей. Доступны оба режима обработки,
          но для этого языка разница между ними ощутимее: режим «Качество» заметно надёжнее на записях со сложным
          звуком.
        </p>
        <p>
          Главная особенность языка для распознавания — тоны: один и тот же слог в разных тонах означает разные вещи.
          Модель различает их по контексту, поэтому на связной речи текст получается корректным, а вот отдельные
          вырванные фразы распознаются менее надёжно.
        </p>
        <p>
          Речь идёт о путунхуа — стандартном варианте языка. Диалекты, сильно отличающиеся от него, распознаются
          заметно хуже.
        </p>
      </>
    ),
    faq: [
      {
        question: "В каком виде приходит текст?",
        answer: "Иероглифами, а не латинской транскрипцией."
      },
      {
        question: "Как модель справляется с тонами?",
        answer:
          "Различает их по контексту предложения. На связной речи это работает надёжно, на коротких вырванных фразах — хуже."
      },
      {
        question: "Поддерживаются ли диалекты?",
        answer:
          "Распознавание рассчитано на путунхуа. Сильно отличающиеся диалекты распознаются заметно менее точно."
      }
    ]
  }
];
